<template>
    <div class="flex min-h-0 flex-1 flex-col">
        <!-- The threads: pick one up, start fresh, let it go. -->
        <div v-if="showHistory" class="mb-3 flex flex-wrap items-center gap-2">
            <USelectMenu
                v-model="selected"
                :items="conversationItems"
                :placeholder="$t('assistant.history')"
                :search-input="{ placeholder: $t('assistant.history_search') }"
                class="w-full sm:w-96"
            />

            <UButton
                :label="$t('assistant.new_conversation')"
                icon="i-lucide-plus"
                color="neutral"
                variant="subtle"
                @click="startNew"
            />

            <UButton
                v-if="conversationId"
                icon="i-lucide-trash-2"
                color="neutral"
                variant="ghost"
                :aria-label="$t('general.delete')"
                @click="removeCurrent"
            />
        </div>

        <!-- The conversation. -->
        <div ref="thread" class="min-h-0 flex-1 overflow-y-auto pr-1">
            <div v-if="!messages.length" class="flex h-full flex-col items-center justify-center gap-4 text-center">
                <UIcon name="i-lucide-sparkles" class="size-10 text-muted" />

                <!-- Milo introduces itself before it says what it can do. The orb
                     is a name in the top bar and nowhere else, and an empty thread is
                     the one place with room to put a sentence behind that name. -->
                <div class="flex flex-col gap-1">
                    <p class="text-base font-medium text-highlighted">{{ $t('assistant.greeting') }}</p>
                    <p class="max-w-md text-sm text-muted">{{ $t('assistant.intro') }}</p>
                </div>

                <div class="flex flex-wrap justify-center gap-2">
                    <UButton
                        v-for="(example, index) in examples"
                        :key="index"
                        :label="example"
                        color="neutral"
                        variant="subtle"
                        size="sm"
                        @click="send(example)"
                    />
                </div>
            </div>

            <div v-else class="flex flex-col gap-3 py-2">
                <template v-for="(message, index) in messages" :key="index">
                    <!-- The turn the portal sent on the desk's behalf after a step
                         ran. A grey line and not a bubble: nobody typed it, and a
                         bubble would be putting words in the supervisor's mouth. -->
                    <p
                        v-if="message.kind === 'continuation'"
                        class="flex items-center gap-1 self-center text-xs text-muted"
                    >
                        <UIcon name="i-lucide-corner-down-right" class="size-3 shrink-0" />
                        {{ $t('assistant.continued') }}
                    </p>

                    <!-- A turn that did not answer, said where the question is.

                         Found live on 2026-09-06, with the API restarting mid-deploy:
                         the only sign was a toast nobody saw, and the question sat in
                         the thread with nothing under it. The next question therefore
                         opened a NEW conversation — the failed turn had never brought
                         back a conversation id — and Milo answered, truthfully, that no
                         question had been asked. A failure has to live in the thread. -->
                    <div
                        v-else-if="message.failed"
                        class="max-w-[85%] flex flex-col items-start gap-2 self-start"
                    >
                        <div class="flex items-start gap-2 rounded-lg bg-error/10 px-3 py-2 text-sm text-error">
                            <UIcon name="i-lucide-circle-alert" class="mt-0.5 size-4 shrink-0" />
                            <span>{{ message.text }}</span>
                        </div>

                        <UButton
                            v-if="ownThread && canResend"
                            :label="$t('assistant.retry')"
                            icon="i-lucide-rotate-ccw"
                            color="neutral"
                            variant="subtle"
                            size="xs"
                            :disabled="loading"
                            @click="resend"
                        />
                    </div>

                    <div
                        v-else
                        class="max-w-[85%] flex flex-col gap-2"
                        :class="message.role === 'user' ? 'self-end' : 'self-start'"
                    >
                        <!-- The text is drawn segment by segment (AssistantText.vue)
                             so that the addresses in it are followable. There is no
                             markdown here and there never was: the bubble is the
                             assistant's own words with its own line breaks, and the
                             only thing it hides that a person wants is a link. -->
                        <div
                            class="rounded-lg px-3 py-2 text-sm"
                            :class="message.role === 'user' ? 'bg-primary/10' : 'bg-elevated/60'"
                        ><AssistantText :text="message.text" /></div>

                        <!-- Several things to do are a numbered checklist; one is
                             the card it has always been. Both draw the same
                             component, so a plan's step and a lone proposal look
                             alike, which they are. -->
                        <AssistantPlan
                            v-if="message.steps && message.steps.length > 1"
                            :steps="message.steps"
                            :own-thread="ownThread"
                            :deciding="deciding"
                            @decide="(step, confirm) => decideStep(message, step, confirm)"
                        />

                        <AssistantOperationCard
                            v-else-if="message.operation"
                            :operation="message.operation"
                            :actionable="ownThread"
                            :deciding="deciding === message.operation.id"
                            @decide="confirm => decide(message.operation!, confirm)"
                        />
                    </div>
                </template>

                <!-- The wait, and after five seconds a second line about it.

                     A turn that reaches for several tools is several model calls, and
                     the ones worth asking are the slow ones: "premakni celo skupino za
                     en dan" reads the group, the calendar and the availability before
                     it answers. A spinner that has said "Preverjam podatke …" for
                     forty seconds reads as a page that has hung, and somebody reloads
                     it — which throws away a turn that was about to arrive. -->
                <div v-if="loading" class="flex items-start gap-2 self-start px-3 py-2 text-sm text-muted">
                    <UIcon name="i-lucide-loader-circle" class="mt-0.5 size-4 shrink-0 animate-spin" />
                    <span>
                        {{ $t('assistant.thinking') }}
                        <span v-if="stillWorking" class="block text-xs">{{ $t('assistant.still_working') }}</span>
                    </span>
                </div>
            </div>
        </div>

        <!-- What the screen behind this one is about (libs/assistantContext.ts).
             Above the box rather than inside the answer, because it is part of the
             question being asked and the person has to be able to take it off. -->
        <div v-if="ownThread && pageContext" class="mt-3 flex">
            <UButton
                :label="$t('assistant.on_page', { subject: pageContext.label })"
                :title="$t('assistant.on_page_hint')"
                :aria-label="$t('assistant.on_page_drop', { subject: pageContext.label })"
                icon="i-lucide-crosshair"
                trailing-icon="i-lucide-x"
                color="neutral"
                variant="subtle"
                size="xs"
                class="max-w-full"
                :ui="{ label: 'truncate' }"
                @click="dismissAssistantContext"
            />
        </div>

        <!-- The question. Another person's thread is read, not continued.
             A native <form> — CLAUDE.md deviation 27 says why. -->
        <form v-if="ownThread" class="mt-3 flex items-end gap-2" @submit.prevent="send()">
            <UTextarea
                v-model="input"
                :rows="2"
                autoresize
                :maxrows="6"
                :placeholder="$t('assistant.placeholder')"
                :disabled="loading"
                class="w-full"
                @keydown.enter.exact.prevent="send()"
            />

            <UButton
                type="submit"
                icon="i-lucide-send-horizontal"
                size="lg"
                :loading="loading"
                :disabled="!input.trim() && !loading"
            />
        </form>

        <p v-else class="mt-3 text-sm text-muted">{{ $t('assistant.read_only') }}</p>

        <p class="mt-2 text-xs text-muted">{{ $t('assistant.disclaimer') }}</p>
    </div>
</template>

<script setup lang="ts">
    import { computed, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue'
    import { useI18n } from 'vue-i18n'

    import AssistantOperationCard from '@/components/AssistantOperationCard.vue'
    import AssistantPlan from '@/components/AssistantPlan.vue'
    import AssistantText from '@/components/AssistantText.vue'
    import { reactMilo, useAssistant } from '@/libs/assistant'
    import { assistantContextForTurn, dismissAssistantContext } from '@/libs/assistantContext'
    import { Errors, parseError, parseErrorCode } from '@/libs/error'
    import { PrintError } from '@/libs/toast'
    import { useAppStore } from '@/stores/app'
    import { useFetchStore } from '@/stores/fetch'
    import type {
        IChatConversationRow, IChatMessage, IChatOperation, IChatReply, IChatStep,
        IChatTurnError, IChatTurnStarted, IChatTurnView,
    } from '@/types/Chat'

    /**
     * The conversation itself: the thread, the proposals, the question box.
     *
     * The reception portal's component of the same name, copied without changes worth
     * naming (docs/AI.md § "Milo v portalu sobaric", CLAUDE.md deviation 29). A proposal
     * card that behaved one way there and another way here would be two products, and
     * the day the two portals share a package this file has to be one file.
     *
     * There, it is drawn by both the drawer and the full `/pomocnik` page and the props
     * below exist to tell the two frames apart. Here there is only the drawer, so
     * `showAll` and `showHistory` are never turned on — kept all the same, because the
     * copy is meant to be moved into a shared package unchanged and a prop deleted here
     * is a difference somebody has to notice and put back.
     */

    /**
     * How long a wait may go unexplained, in milliseconds.
     *
     * Five seconds. Under it, the ordinary spinner is the honest thing to show: most
     * turns answer inside it and a second line would be noise on every question. Past
     * it, the turn is one that went and read something — several model calls, each with
     * its own round trip — and a spinner alone starts reading as a page that has hung.
     * The line it then shows names a minute, which is the honest order of magnitude: the
     * server allows five and the tool loop stops long before that.
     */
    const stillWorkingAfter = 5000

    /**
     * How often the portal asks again for an answer that is still being written, and for
     * how long, in milliseconds.
     *
     * Two seconds is what the API's own Retry-After says, and the poll is cheap: one
     * record, already paid for. Five minutes is the SERVER's budget for a turn
     * (DataStructures.ChatTurnBudget) — asking for longer would be waiting on something
     * that has been abandoned, and giving up sooner would throw away an answer that is
     * about to arrive, which is the whole failure this replaces.
     */
    const turnPollEvery  = 2000
    const turnPollBudget = 5 * 60 * 1000

    interface ConversationItem {
        label: string
        value: string
    }

    const props = withDefaults(defineProps<{
        /** List everybody's threads, not only this account's (chat:admin, page only). */
        showAll?: boolean
        /** Draw the thread picker in here. The drawer has no room for one and puts
         *  "Nov pogovor" in its header instead. Never set in this portal: there is no
         *  full page here for a list of threads to belong to. */
        showHistory?: boolean
        /** Whether the person can actually SEE this conversation right now — the
         *  drawer's own open state, since the drawer stays mounted while it is shut. It
         *  decides one thing: whether an answer that lands counts as unread on the orb. */
        visible?: boolean
    }>(), {
        showAll:     false,
        showHistory: true,
        visible:     true,
    })

    const emit = defineEmits<{
        /** The thread on screen changed — a new one was started, or another opened. */
        'update:conversationId': [id: string]
        /** A proposal actually ran. The page behind the drawer reloads on this. */
        executed: []
    }>()

    const { t }      = useI18n()
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()

    const assistant = useAssistant()

    const thread         = useTemplateRef<HTMLElement>('thread')
    const messages       = ref<IChatMessage[]>([])
    const input          = ref('')
    const loading        = ref(false)

    /** Whether the wait has gone on long enough to be worth explaining. See stillWorkingAfter. */
    const stillWorking   = ref(false)
    const conversationId = ref('')
    const conversations  = ref<IChatConversationRow[]>([])
    const ownerId        = ref('')

    /** Whose thread is on screen — only the holder's own may be continued. */
    const ownThread = computed((): boolean => !ownerId.value || ownerId.value === appStore.userData?.id)

    const pageContext = computed(() => assistantContextForTurn() ?? null)

    const conversationItems = computed((): ConversationItem[] => conversations.value.map(row => ({
        label: (props.showAll && row.user_name ? `${row.user_name}: ` : '') + row.title,
        value: row.id,
    })))

    const selected = computed({
        get: (): ConversationItem | undefined => conversationItems.value.find(item => item.value === conversationId.value),
        set: (item: ConversationItem | undefined) => { if (item) void loadConversation(item.value) },
    })

    const examples = computed((): string[] => [
        t('assistant.example_arrivals'),
        t('assistant.example_availability'),
        t('assistant.example_revenue'),
    ])

    /** The orb in the top bar is the same conversation seen from outside: a proposal
     *  nobody has decided is exactly what its "attention" state means. */
    const proposalWaiting = computed((): boolean => ownThread.value && messages.value.some(message =>
        message.operation?.status === 'proposed'
        || (message.steps ?? []).some(step => step.operation?.status === 'proposed')))

    watch(proposalWaiting, waiting => { assistant.pending.value = waiting }, { immediate: true })
    watch(loading, value => { assistant.busy.value = value })
    watch(conversationId, id => emit('update:conversationId', id))

    // A request whose component went away is not a request still in flight.
    onUnmounted(() => { assistant.busy.value = false })

    const scrollDown = async () => {
        await nextTick()
        thread.value?.scrollTo({ top: thread.value.scrollHeight })
    }

    const loadConversations = async () => {
        const query = props.showAll ? '?all=1' : ''

        const { statusCode, data } = await fetchStore
            .fetch(`/properties/${appStore.propertyId}/chat/conversations${query}`)
            .get()
            .json<IChatConversationRow[]>()

        if (statusCode.value === 200) conversations.value = data.value ?? []
    }

    const loadConversation = async (id: string) => {
        const { statusCode, data } = await fetchStore
            .fetch(`/properties/${appStore.propertyId}/chat/conversations/${id}`)
            .get()
            .json<{ id: string, user_id: string, messages: IChatMessage[], running_turn_id?: string }>()

        if (statusCode.value !== 200 || !data.value) {
            PrintError(parseError(data.value, t('errors.generic_error')))

            return
        }

        conversationId.value = data.value.id
        ownerId.value        = data.value.user_id
        messages.value       = data.value.messages ?? []
        await scrollDown()

        // AN ANSWER STILL ON ITS WAY. The thread carries the turn it is waiting for, so
        // a page reloaded mid-turn goes back to waiting rather than showing a question
        // nothing ever answers — which is the state this whole mechanism exists to end.
        // Only in one's own thread: another person's is read, not waited on.
        if (data.value.running_turn_id && ownThread.value) void resumeTurn(data.value.running_turn_id)
    }

    const startNew = () => {
        conversationId.value = ''
        ownerId.value        = ''
        messages.value       = []
        input.value          = ''
    }

    const removeCurrent = async () => {
        const { response, data } = await fetchStore
            .fetch(`/properties/${appStore.propertyId}/chat/conversations/${conversationId.value}`)
            .delete()
            .json()

        if (response.value?.status === 200) {
            startNew()
            await loadConversations()
        } else {
            PrintError(parseError(data.value, t('errors.generic_error')))
        }
    }

    /**
     * Why the turn did not answer, in the desk's own words.
     *
     * Two sentences and not one, because there are two situations and only one of them
     * is worth waiting out. A refusal the SERVICE actually made is its own — the
     * server's sentence, through parseError — and the way on is to reword or to fix
     * what it names. Everything below is the service NOT BEING THERE, which is what a
     * deploy looks like from a browser, and the only honest thing to say about it is
     * "in half a minute".
     *
     * Three ways that arrives, and the third is the one that catches people:
     *
     *   · no response at all — the connection never completed;
     *   · 502 / 503 / 504 — a gateway answering for a service that is not up;
     *   · **200 that is not an answer** — the edge (NPM) serves its own maintenance page
     *     with a 200 and an HTML body, so `.json()` yields nothing while the status line
     *     says everything is fine. A turn that reaches here carrying no `text` never
     *     reached the model, whatever number came back with it.
     */
    const failureReason = (response: Response | null, body: unknown): string => {
        // The service's OWN 504 carries a sentence (the turn ran out of time); a
        // gateway's 504 carries HTML and no sentence. Only the first is a question to
        // narrow — the second is still "not reachable".
        if (response?.status === 504 && typeof (body as { error?: { message?: string } })?.error?.message === 'string') {
            return t('assistant.failed_timeout')
        }

        return !response || response.status === 200 || [502, 503, 504].includes(response.status)
            ? t('assistant.failed_unreachable')
            : parseError(body, t('assistant.failed'))
    }

    /**
     * The questions this thread has asked and has no answer to, oldest first.
     *
     * Everything since the last answer. A failed turn leaves its question standing —
     * the service never saw it — and whatever is typed next has to carry it, or the
     * thread only LOOKS like a conversation: the model is handed the second question
     * with no sight of the first. That is exactly what happened on 2026-09-06.
     *
     * The continuation line carries no text of anybody's and is skipped.
     */
    const unansweredQuestions = (): string[] => {
        const texts: string[] = []

        for (let index = messages.value.length - 1; index >= 0; index--) {
            const message = messages.value[index]

            if (message.role === 'assistant' && !message.failed) break

            if (message.role === 'user' && message.kind !== 'continuation' && message.text) texts.unshift(message.text)
        }

        return texts
    }

    /** Whether "Pošlji znova" has anything to send. It has not after a CONTINUATION
     *  failed: nobody typed that turn — it is this service's own note about a step —
     *  so the way on from there is the next question and not this button. */
    const canResend = computed((): boolean => unansweredQuestions().length > 0)

    /**
     * One turn's text out of the questions it carries.
     *
     * A single question is itself, unchanged and undressed: the ordinary turn must not
     * pay for the exception. Several are labelled, because an unlabelled concatenation
     * is one rambling question and gets one answer, to its end.
     *
     * The labels are Slovenian and deliberately not translated. They are prompt text
     * the model reads, not a screen anybody looks at, and they are STORED with the turn
     * (docs/AI.md §20) — a thread whose question was worded by whichever UI language
     * the sender happened to have on would say something different to whoever opens it
     * next.
     */
    const combinedQuestion = (texts: string[]): string => texts.length < 2
        ? texts[0] ?? ''
        : [
            ...texts.slice(0, -1).map(text => `Prejšnje vprašanje, na katero še ni bilo odgovora: ${text}`),
            `Novo vprašanje: ${texts[texts.length - 1]}`,
        ].join('\n\n')

    /** The failure notes an arriving answer settles. Removed rather than greyed out:
     *  what each of them says is that an answer is missing, and it is not missing any
     *  more. The QUESTIONS stay where they were — they are what was asked. */
    const dropFailureNotes = () => { messages.value = messages.value.filter(message => !message.failed) }

    /** One turn's outcome, however long it took and however many requests it took to
     *  collect it: the answer, or the sentence to draw under the question. */
    interface TurnOutcome {
        reply?:   IChatReply
        failure?: string
    }

    /**
     * What a FAILED turn says, in the desk's own words wherever the portal has them.
     *
     * The two codes are the failures NOBODY MADE: the turn used its whole budget, or the
     * process working on it went away mid-answer (a deploy). Both carry an English
     * sentence from the service, and both have a code precisely so this portal can say
     * the same thing in the language the screen is in. Anything else is a real refusal
     * and the service's own sentence is the best one there is.
     */
    const turnFailureReason = (error: IChatTurnError | null): string => {
        if (error?.code === Errors.AssistantTurnTimedOut) return t('assistant.failed_timeout')

        if (error?.code === Errors.AssistantTurnLost) return t('assistant.failed_lost')

        return error?.message || t('assistant.failed')
    }

    /**
     * Waits for an answer that outlived the request that asked for it.
     *
     * # Why the losses in here are not failures
     *
     * Because the answer is not in this browser. The turn is being worked out on the
     * server, on a context detached from any request (docs/AI.md §31), so a connection
     * that drops, a gateway answering 502 through a deploy, or a reload are all things to
     * ask AGAIN through — the answer is waiting on the other side of them. Before this,
     * every one of those threw away a turn the hotel had already been billed for.
     *
     * What ends the wait is an answer, a refusal, a turn the service no longer knows
     * about, or the server's own budget running out.
     */
    const waitForTurn = async (turnId: string): Promise<TurnOutcome> => {
        const until = Date.now() + turnPollBudget

        while (Date.now() < until) {
            await new Promise(resolve => { window.setTimeout(resolve, turnPollEvery) })

            const { response, data } = await fetchStore
                .fetch(`/properties/${appStore.propertyId}/chat/turns/${turnId}`)
                .get()
                .json<IChatTurnView>()

            const status = response.value?.status ?? 0

            // Still working (202), or the service is momentarily not there. Ask again.
            if (status === 0 || status === 202 || [502, 503, 504].includes(status)) continue

            if (status === 200 && data.value?.status === 'done' && data.value.reply) {
                return { reply: data.value.reply }
            }

            if (status === 200 && data.value?.status === 'failed') {
                return { failure: turnFailureReason(data.value.error ?? null) }
            }

            // 404 and anything else: this turn is not answerable any more, and asking
            // again would only keep the spinner up in front of somebody.
            return { failure: t('assistant.failed_lost') }
        }

        return { failure: t('assistant.failed_timeout') }
    }

    /**
     * Asks one turn and comes back with its answer, whichever way the answer arrives.
     *
     * TWO WAYS, and the first is still the ordinary one: the API waits up to forty-five
     * seconds for the turn it started and answers the reply itself, exactly as it always
     * did. A question that outlives that wait is answered 202 with the turn's name, and
     * this asks for it every two seconds until it has it (docs/AI.md §31).
     *
     * The reason for the pair is the EDGE: it closes any request at ninety seconds. On
     * 2026-09-09 a long question — a couple, a garage, a late check-out, a charger, full
     * board — was killed by exactly that with the answer nearly composed, and the desk
     * was shown „strežnik ni dosegljiv“ for a service that was up all along.
     */
    const ask = async (body: Record<string, unknown>): Promise<TurnOutcome> => {
        // NO CLIENT-SIDE TIMEOUT, deliberately, and the fetch store sets none either
        // (stores/fetch.ts creates its client without one). The turn is bounded on the
        // SERVER; a browser giving up first would abandon a turn that was about to
        // arrive, and the model call it abandoned would already have been paid for.
        const { response, data } = await fetchStore
            .fetch(`/properties/${appStore.propertyId}/chat`)
            .post({ conversation_id: conversationId.value || undefined, ...body })
            .json<IChatReply & Partial<IChatTurnStarted>>()

        if (response.value?.status === 202 && data.value?.turn_id) {
            return waitForTurn(data.value.turn_id)
        }

        if (response.value?.status === 200 && data.value?.text) {
            return { reply: data.value }
        }

        return { failure: failureReason(response.value, data.value) }
    }

    /** One turn, whichever kind. The body is what separates a typed question from the
     *  note the portal sends after a step ran; everything else about a turn is the same,
     *  which is also why the continuation is counted in the usage ledger like any other. */
    const turn = async (body: Record<string, unknown>): Promise<boolean> => {
        loading.value      = true
        stillWorking.value = false
        await scrollDown()

        const hint = window.setTimeout(() => { stillWorking.value = true }, stillWorkingAfter)

        const outcome = await ask(body)

        window.clearTimeout(hint)

        return settle(outcome)
    }

    /**
     * Picks up an answer that was already being written when this thread was opened.
     *
     * The case is a receptionist who reloads the page, or opens the thread on another
     * screen, while a turn is running — which the wait this feature is about is long
     * enough to make ordinary. The thread says so itself (`running_turn_id` on the
     * conversation read), so the portal goes back to waiting instead of drawing a
     * question with nothing under it.
     */
    const resumeTurn = async (turnId: string) => {
        if (loading.value) return

        loading.value      = true
        // Straight to the second line: whatever this turn is, it has been going longer
        // than the five seconds that line waits for.
        stillWorking.value = true
        await scrollDown()

        await settle(await waitForTurn(turnId))
    }

    /** Draws whatever the turn came back with — the answer under the question, or the
     *  reason there is none — and returns whether it was answered. */
    const settle = async (outcome: TurnOutcome): Promise<boolean> => {
        const answered = !!outcome.reply

        if (outcome.reply) {
            conversationId.value = outcome.reply.conversation_id
            ownerId.value        = ownerId.value || appStore.userData?.id || ''

            // Whatever failed before this is answered now, and the answer takes its
            // place — under the question, which is where it was always going to go.
            dropFailureNotes()

            messages.value.push({
                role:      'assistant',
                text:      outcome.reply.text,
                operation: outcome.reply.operation,
                steps:     outcome.reply.steps,
            })

            assistant.answered(props.visible)

            await loadConversations()
        } else {
            // The question stays in the thread, and so does the reason. A toast is gone
            // in seconds and may be behind the drawer; the thread is the one place the
            // person is actually looking, and it is where the button to ask again belongs.
            const reason = outcome.failure ?? t('assistant.failed')

            messages.value.push({ role: 'assistant', text: reason, failed: true })

            // The toast only where the thread is NOT in front of somebody — the drawer
            // shut, the answer failing behind it. With the thread open the note above is
            // already under their eyes (the reception portal's own rule, v1.569.4).
            if (!props.visible) PrintError(reason)
        }

        loading.value      = false
        stillWorking.value = false

        // Milo's face, and only after `loading` is false. The head returns to its
        // PERSISTENT state when a one-shot expression ends, so a reaction played while
        // the state still said "thinking" would leave him thinking about a turn that
        // had already failed. The head waits a tick for exactly this reason
        // (components/app/MiloHead.vue), and this line is the other half of it.
        if (!answered) reactMilo('error')

        await scrollDown()

        return answered
    }

    const send = async (preset?: string) => {
        const text = (preset ?? input.value).trim()

        if (!text || loading.value) return

        input.value = ''

        // Read BEFORE the new question joins them: what this turn owes the model is
        // everything that was asked and never answered, and then this.
        const pending = unansweredQuestions()

        messages.value.push({ role: 'user', text })

        // The screen the person is looking at rides along with the question, unless
        // they have taken the chip off. Never with a continuation: that turn is this
        // service's own note about a step, and the page behind it is not part of it.
        await turn({ text: combinedQuestion([...pending, text]), context: assistantContextForTurn() })
    }

    /**
     * Asks again, with exactly what is still unanswered and the page it was asked from.
     *
     * No new bubble: the question is already in the thread, and writing it a second
     * time would be the portal claiming somebody asked twice. When the answer arrives
     * the failure note goes and the answer stands in its place, under the question.
     */
    const resend = async () => {
        if (loading.value) return

        const pending = unansweredQuestions()

        if (!pending.length) return

        await turn({ text: combinedQuestion(pending), context: assistantContextForTurn() })
    }

    /**
     * Carries the plan on after a step has been executed.
     *
     * Nobody types this and nobody is asked for it: the desk already said what they
     * wanted when they made the request, and the confirmation they have just pressed is
     * the permission. What it asks for is the NEXT step's proposal — which still arrives
     * as a card with its own Confirm button, because a person decides every change here.
     *
     * The API writes the sentence the model reads (Logic.chatContinuationNote): the
     * portal names the operation and nothing else. A browser telling a model that a room
     * had been closed is exactly the claim this product does not let a browser make.
     */
    const continueAfter = async (operationId: string) => {
        if (loading.value) return

        messages.value.push({ role: 'user', text: '', kind: 'continuation' })

        await turn({ kind: 'continuation', after_operation_id: operationId })
    }

    const deciding = ref('')

    /**
     * Confirms or declines one proposal; the card then shows what happened —
     * INLINE, not only as a toast the desk has already dismissed by the time they
     * look back at the proposal. Found live, 2026-09-03: a room-close proposal
     * refused for a genuine reason (the room was booked) read "Milo ni mogel
     * odgovoriti. Poskusi znova." three times, because a failed confirm/decline
     * left the card exactly as it was — still offering Confirm/Decline on an
     * operation the server had already settled as failed.
     */
    const decide = async (operation: IChatOperation, confirm: boolean): Promise<boolean> => {
        deciding.value = operation.id

        const verb = confirm ? 'confirm' : 'decline'
        const { response, data } = await fetchStore
            .fetch(`/properties/${appStore.propertyId}/chat/operations/${operation.id}/${verb}`)
            .post({})
            .json<IChatOperation>()

        let executed = false
        /** The press did not settle the proposal — the server refused it, or never
         *  answered. A DECLINE is neither: it is what the person asked for. */
        let failed   = false

        if (response.value?.status === 200) {
            operation.status  = confirm ? (data.value?.status ?? 'executed') : 'declined'
            operation.result  = data.value?.result ?? ''
            operation.outcome = data.value?.outcome ?? null
            executed          = operation.status === 'executed'
        } else if (response.value) {
            // The server answered — its own reason belongs on the card. "Already
            // decided" (IncorrectStatus: the CAS in Logic.ConfirmChatOperation had
            // already settled this operation, most often a retry after an earlier
            // press actually went through) reads its own word rather than the
            // generic failure — retrying again would answer exactly the same way.
            const alreadyDecided = parseErrorCode(data.value) === Errors.IncorrectStatus

            failed           = true
            operation.status = 'failed'
            operation.result = alreadyDecided ? t('assistant.already_decided') : parseError(data.value, t('assistant.failed'))
        } else {
            // No response at all — genuinely unknown whether this reached the
            // server. The card stays exactly as it was and a second press is
            // safe either way: Logic.ConfirmChatOperation's own claim-before-run
            // (invariant 5) is what makes that true.
            PrintError(t('assistant.failed'))
            failed = true
        }

        deciding.value = ''

        // What the desk has just pressed, on Milo's face. Said here rather than in the
        // card, because this is the one place that knows the outcome of every route to
        // a decision — a lone proposal and a step of a plan alike.
        if (executed)    reactMilo('success')
        else if (failed) reactMilo('error')

        // The screen underneath was loaded before this ran. Said once, here, so that
        // every route to an execution — a lone card, a step of a plan — announces it.
        if (executed) {
            appStore.assistantExecuted()
            emit('executed')
        }

        return executed
    }

    /**
     * Decides one step of a plan and then keeps the plan moving.
     *
     * Two outcomes, and neither of them asks the desk for anything: the next step
     * already has a proposal and simply becomes pressable (AssistantPlan decides that
     * from depends_on), or it is a PLANNED step whose proposal could not be built while
     * this one was undone — and that one is asked for now, in a continuation turn.
     *
     * This is the whole of what was missing on 2026-09-05. The desk confirmed the move,
     * read "Izvedeno", and then sat in front of an assistant waiting to be asked the
     * same question a second time.
     */
    const decideStep = async (message: IChatMessage, step: IChatStep, confirm: boolean) => {
        if (!step.operation) return

        const executed = await decide(step.operation, confirm)

        if (!executed) return

        const next = message.steps?.find(one => one.depends_on === step.number)

        if (next && !next.operation) await continueAfter(step.operation.id)
    }

    onMounted(loadConversations)

    // Switching the house switches whose threads there are to list.
    watch(() => appStore.propertyId, () => { startNew(); void loadConversations() })

    // The oversight toggle lives on the page above; the list it changes is here.
    watch(() => props.showAll, () => void loadConversations())

    /** What the drawer's header calls the open thread. A thread that has not been
     *  answered yet has no title of its own, and "Nov pogovor" is the honest one. */
    const conversationTitle = computed((): string =>
        conversations.value.find(row => row.id === conversationId.value)?.title || t('assistant.new_conversation'))

    defineExpose({ startNew, loadConversation, loadConversations, conversationId, conversationTitle })
</script>
